import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper, Grid, Skeleton, Card } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Line, Bar } from 'react-chartjs-2';
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import { chartColors, baseURL } from "../../utils/StaticVariables";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "./OrderFilter";

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { color: 'gray' } },
    title: { display: false }
  },
  scales: {
    x: { ticks: { color: 'gray' } },
    y: { ticks: { color: 'gray' } }
  }
};

const ExecSummaryChart = () => {
  const [dataList, setDataList] = useState(null);
  const [loading, setLoading] = useState(false);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { ...filter };
        if (params.locations) { params.location = params.locations; delete params.locations; }
        if (params.buildings) { params.building = params.buildings; delete params.buildings; }
        if (params.zones) { params.zone = params.zones; delete params.zones; }

        const { data } = await axios.get(`${baseURL}shoplifting/dashboard/executive-summary`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        setDataList(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [filter, token]);

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  const trendLabels = dataList?.trend?.map(t => t.date) || [];
  const trendValues = dataList?.trend?.map(t => t.total_value || 0) || [];
  const trendCounts = dataList?.trend?.map(t => t.incident_count || 0) || [];

  const locLabels = dataList?.incidents_by_location?.map(l => l.location) || [];
  const locCounts = dataList?.incidents_by_location?.map(l => l.incident_count || 0) || [];

  return (
    <Card sx={{ p: 2, height: '100%', bgcolor: 'background.paper', mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
         <Typography variant="h6">Executive Summary</Typography>
         <DesBtn text="Filter" handle={openFilter}>
          <FilterAltOutlinedIcon />
         </DesBtn>
      </Box>

      {loading || !dataList ? (
        <Skeleton variant="rectangular" height="400px" sx={{ borderRadius: 2 }} />
      ) : (
        <Grid container spacing={3} sx={{ height: 400 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle1" mb={2}>Incident Trend & Lost Value</Typography>
              <Box sx={{ height: 320 }}>
                <Line 
                  data={{
                    labels: trendLabels.length ? trendLabels : ['No Data'],
                    datasets: [
                      { label: 'Lost Value ($)', data: trendValues.length ? trendValues : [0], borderColor: '#ff6384', backgroundColor: '#ff6384', yAxisID: 'y' },
                      { label: 'Incident Count', data: trendCounts.length ? trendCounts : [0], borderColor: '#4bc0c0', backgroundColor: '#4bc0c0', yAxisID: 'y1' }
                    ]
                  }}
                  options={{
                    ...commonOptions,
                    scales: {
                      ...commonOptions.scales,
                      y: { type: 'linear', display: true, position: 'left' },
                      y1: { type: 'linear', display: true, position: 'right', grid: { drawOnChartArea: false } }
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, height: '100%', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle1" mb={2}>Incident Frequency by Location</Typography>
              <Box sx={{ height: 320 }}>
                <Bar 
                  data={{
                    labels: locLabels.length ? locLabels : ['No Data'],
                    datasets: [{ label: 'Incidents', data: locCounts.length ? locCounts : [0], backgroundColor: chartColors }]
                  }}
                  options={commonOptions}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Card>
  );
};

export default ExecSummaryChart;
